import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  Typography,
  Box,
  Paper,
  LinearProgress,
  IconButton,
  Card,
  CardContent,
  FormHelperText,
  CircularProgress,
} from '@material-ui/core';
import {
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
  Visibility as PreviewIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import {
  setWorkInstructionDialogOpen,
  setWorkInstructionValidation,
  setSelectedWorkInstruction,
  setDocumentUploadStart,
  clearDocumentErrors,
  setDocumentPreviewOpen,
} from '../../store/reducers/workInstructionsSlice';
import { workInstructionsApi } from '../../services/workInstructionsApi';

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%',
  },
  formSection: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  uploadArea: {
    border: `2px dashed ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.3s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&.dragover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  uploadAreaActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
  fileInput: {
    display: 'none',
  },
  documentCard: {
    marginTop: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  documentActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  progressContainer: {
    marginTop: theme.spacing(2),
  },
}));

const WorkInstructionsEdit: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  
  const {
    workInstructions: { selectedWorkInstruction, validation },
    documents: { uploading, uploadProgress, uploadError },
    ui: { workInstructionDialogOpen },
  } = useSelector((state: RootState) => state.workInstructions);

  const [formData, setFormData] = useState({
    Name: '',
    Description: '',
    Active: true,
    Document: null as any,
  });

  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const [dragActive, setDragActive] = useState(false);

  // Initialize form data when work instruction is selected
  useEffect(() => {
    if (selectedWorkInstruction) {
      setFormData({
        Name: selectedWorkInstruction.Name || '',
        Description: selectedWorkInstruction.Description || '',
        Active: selectedWorkInstruction.Active ?? true,
        Document: selectedWorkInstruction.Document || null,
      });
    } else {
      // Reset form for new work instruction
      setFormData({
        Name: '',
        Description: '',
        Active: true,
        Document: null,
      });
    }
    setLocalErrors({});
    dispatch(setWorkInstructionValidation({}));
    dispatch(clearDocumentErrors());
  }, [selectedWorkInstruction, dispatch]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field-specific errors
    if (localErrors[field]) {
      setLocalErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (validation[field]) {
      dispatch(setWorkInstructionValidation({ ...validation, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.Name.trim()) {
      errors.Name = 'Work Instruction Name is required';
    }

    setLocalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // File upload handling
  const handleFileSelect = (file: File) => {
    const validationErrors = workInstructionsApi.validation.validateDocument(file);
    if (validationErrors.length > 0) {
      setLocalErrors(prev => ({ ...prev, document: validationErrors.join(', ') }));
      return;
    }

    // Clear errors and start upload
    setLocalErrors(prev => ({ ...prev, document: '' }));
    dispatch({ type: 'workInstructions/uploadDocument', payload: file });
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input value to allow selecting the same file again
    event.target.value = '';
  };

  const handleDragEvents = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    handleDragEvents(e);
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    handleDragEvents(e);
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    handleDragEvents(e);
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleRemoveDocument = () => {
    setFormData(prev => ({ ...prev, Document: null }));
  };

  const handleDownloadDocument = async () => {
    try {
      if (formData.Document) {
        await workInstructionsApi.utils.downloadDocumentFile(formData.Document);
      }
    } catch (error) {
      console.error('Failed to download document:', error);
    }
  };

  const handlePreviewDocument = () => {
    if (formData.Document) {
      dispatch(setDocumentPreviewOpen({ open: true, document: formData.Document }));
    }
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const workInstructionData = {
      ...formData,
      Id: selectedWorkInstruction?.Id,
    };

    if (selectedWorkInstruction) {
      dispatch({ type: 'workInstructions/updateWorkInstruction', payload: workInstructionData });
    } else {
      dispatch({ type: 'workInstructions/createWorkInstruction', payload: workInstructionData });
    }
  };

  const handleClose = () => {
    dispatch(setWorkInstructionDialogOpen(false));
    dispatch(setSelectedWorkInstruction(null));
    dispatch(setWorkInstructionValidation({}));
    dispatch(clearDocumentErrors());
    setLocalErrors({});
  };

  const getFieldError = (field: string): string => {
    return localErrors[field] || validation[field] || '';
  };

  // Update form data when document upload is successful
  useEffect(() => {
    // This will be handled by the saga updating the selectedWorkInstruction
  }, []);

  return (
    <Dialog open={workInstructionDialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {selectedWorkInstruction ? 'Edit Work Instruction' : 'Add New Work Instruction'}
      </DialogTitle>
      <DialogContent>
        <form className={classes.form}>
          {/* General Errors */}
          {validation.general && (
            <Typography variant="body2" className={classes.errorText}>
              {validation.general}
            </Typography>
          )}

          {/* Basic Information */}
          <div className={classes.formSection}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Work Instruction Name"
                  value={formData.Name}
                  onChange={(e) => handleInputChange('Name', e.target.value)}
                  error={Boolean(getFieldError('Name'))}
                  helperText={getFieldError('Name')}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={formData.Description}
                  onChange={(e) => handleInputChange('Description', e.target.value)}
                  error={Boolean(getFieldError('Description'))}
                  helperText={getFieldError('Description')}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.Active}
                      onChange={(e) => handleInputChange('Active', e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </div>

          {/* Document Upload */}
          <div className={classes.formSection}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Document
            </Typography>
            
            {!formData.Document && !uploading && (
              <Paper
                className={`${classes.uploadArea} ${dragActive ? classes.uploadAreaActive : ''}`}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragEvents}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById('document-upload-input')?.click()}
              >
                <UploadIcon style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
                <Typography variant="h6" gutterBottom>
                  Upload Document
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Drag and drop a file here, or click to select
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Supported formats: PDF, Word, Text, Images (Max: 10MB)
                </Typography>
                <input
                  id="document-upload-input"
                  type="file"
                  className={classes.fileInput}
                  onChange={handleFileInputChange}
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                />
              </Paper>
            )}

            {uploading && (
              <div className={classes.progressContainer}>
                <Typography variant="body2" gutterBottom>
                  Uploading document...
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" color="textSecondary">
                  {uploadProgress}% complete
                </Typography>
              </div>
            )}

            {formData.Document && (
              <Card className={classes.documentCard}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                      <FileIcon style={{ marginRight: 8, color: '#1976d2' }} />
                      <div>
                        <Typography variant="body1" component="div">
                          {formData.Document.Name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {workInstructionsApi.utils.getFileIcon(formData.Document.DocumentType || '')} 
                          {formData.Document.DocumentType}
                          {formData.Document.Size && ` • ${workInstructionsApi.utils.formatFileSize(formData.Document.Size)}`}
                        </Typography>
                      </div>
                    </Box>
                    <div className={classes.documentActions}>
                      <IconButton
                        size="small"
                        onClick={handlePreviewDocument}
                        title="Preview Document"
                      >
                        <PreviewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleDownloadDocument}
                        title="Download Document"
                      >
                        <DownloadIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleRemoveDocument}
                        title="Remove Document"
                        color="secondary"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  </Box>
                </CardContent>
              </Card>
            )}

            {(uploadError || getFieldError('document')) && (
              <FormHelperText error>
                {uploadError || getFieldError('document')}
              </FormHelperText>
            )}
          </div>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          color="primary" 
          variant="contained"
          disabled={uploading}
        >
          {uploading ? (
            <>
              <CircularProgress size={16} style={{ marginRight: 8 }} />
              Uploading...
            </>
          ) : (
            selectedWorkInstruction ? 'Update' : 'Create'
          )} Work Instruction
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkInstructionsEdit; 